import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import BugReportButton from './BugReportButton';
import LandingPage from '../pages/LandingPage';
import LoginPage from '../pages/LoginPage';
import ResetPasswordPage from '../pages/ResetPasswordPage';
import SetPinPage from '../pages/SetPinPage';
import OnboardingPage from '../pages/OnboardingPage';
import DashboardPage from '../pages/DashboardPage';
import JournalPage from '../pages/JournalPage';
import MeetingLogPage from '../pages/MeetingLogPage';
import ContentLibraryPage from '../pages/ContentLibraryPage';
import DietRecoveryPage from '../pages/DietRecoveryPage';
import AdminInboxPage from '../pages/AdminInboxPage';
import GroupChatPage from '../pages/GroupChatPage';
import DirectMessagesPage from '../pages/DirectMessagesPage';
import BotChatPage from '../pages/BotChatPage';
import ProfilePage from '../pages/ProfilePage';
import RelapseRecoveryPage from '../pages/RelapseRecoveryPage';
import WomenOnlyPage from '../pages/WomenOnlyPage';
import MenOnlyPage from '../pages/MenOnlyPage';
import SuperAdminPage from '../pages/SuperAdminPage';
import PrivacyPolicyPage from '../pages/PrivacyPolicyPage';
import TermsOfServicePage from '../pages/TermsOfServicePage';
import HIPAAPrivacyPage from '../pages/HIPAAPrivacyPage';
import JourneyPage from '../pages/JourneyPage';
import PersonalHistoryPage from '../pages/PersonalHistoryPage';
import DrugScreenPage from '../pages/DrugScreenPage';
import NotificationsPage from '../pages/NotificationsPage';
import StickmanAssessmentPage from '../pages/StickmanAssessmentPage';
import IntroductionPage from '../pages/IntroductionPage';
import SelfWorkPage from '../pages/SelfWorkPage';
import SelfWorkWorkbookPage from '../pages/SelfWorkWorkbookPage';
import ARPUploadsPage from '../pages/ARPUploadsPage';
import FamilyCoursePage from '../pages/FamilyCoursePage';
import IndividualCoursePage from '../pages/IndividualCoursePage';
import ReadinessCheckPage from '../pages/ReadinessCheckPage';
// InterimCrisisResourcesPage retired — /crisis-resources redirects
// to the single /get-help directory (Sep 2026, Tudor Fix 2).
import SuperAdminFamilyCoursePage from '../pages/SuperAdminFamilyCoursePage';
import CaseManagementPage from '../pages/CaseManagementPage';
import AdmissionAssessmentPage from '../pages/AdmissionAssessmentPage';
import SelfWorkTaskPage from '../pages/SelfWorkTaskPage';
import ReferencesPage from '../pages/ReferencesPage';
import WeeklyReadingPage from '../pages/WeeklyReadingPage';
import LessonsPage from '../pages/LessonsPage';
import LessonViewPage from '../pages/LessonViewPage';
import ZoomMeetingsPage from '../pages/ZoomMeetingsPage';
import MeetingVerificationPage from '../pages/MeetingVerificationPage';
import BadgesPage from '../pages/BadgesPage';
import GamesPage from '../pages/GamesPage';
// Legacy React games (Wordscape / Colour Sort / Quote Builder) retired
// 9 Oct 2026 in favour of the 6-game static suite under /games/.
import UserManualPage from '../pages/UserManualPage';
import ReEngagementPage from '../pages/ReEngagementPage';
import RelapseMessagePage from '../pages/RelapseMessagePage';
import RelapsePreventionPage from '../pages/RelapsePreventionPage';
import SuperAdminUdsPage from '../pages/SuperAdminUdsPage';
import MyReportsPage from '../pages/MyReportsPage';
import SuperAdminReportsPage from '../pages/SuperAdminReportsPage';
import PathologyUploadPage from '../pages/PathologyUploadPage';
import ProtectedRoute from './ProtectedRoute';
import PublicTourPage from '../pages/PublicTourPage';
import GetHelpPage from '../pages/GetHelpPage';
import SuperAdminCheckinReport from '../pages/SuperAdminCheckinReport';
// InterimCrisisResourcesPage import removed — /crisis-resources now
// redirects to the single /get-help directory (Sep 2026, Tudor Fix 2).

// Smart /diet redirect: with ?meal=<id> goes to the browser tab that auto-opens the modal,
// otherwise lands on the main Diet & Recovery page.
function DietRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const mealId = params.get('meal');
  if (mealId) {
    return <Navigate to={`/library?tab=diet&meal=${encodeURIComponent(mealId)}`} replace />;
  }
  return <Navigate to="/diet-recovery" replace />;
}

function AppRouter() {
  return (
    <>
      <Routes>
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Public 60-second product tour for marketing site visitors. No auth required. */}
      <Route path="/tour" element={<PublicTourPage />} />
      <Route path="/set-pin" element={<ProtectedRoute><SetPinPage /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/journal" element={<ProtectedRoute><JournalPage /></ProtectedRoute>} />
      {/* /mental-health route retired: the old testing section was removed
          in Sep 2026. The new privacy-first check-in lives on the Dashboard. */}
      <Route path="/mental-health" element={<Navigate to="/dashboard" replace />} />
      <Route path="/meetings" element={<ProtectedRoute><MeetingLogPage /></ProtectedRoute>} />
      <Route path="/meeting-log" element={<ProtectedRoute><MeetingLogPage /></ProtectedRoute>} />
      <Route path="/meetings/zoom" element={<ProtectedRoute><ZoomMeetingsPage /></ProtectedRoute>} />
      <Route path="/meetings/verify" element={<ProtectedRoute><MeetingVerificationPage /></ProtectedRoute>} />
      <Route path="/badges" element={<ProtectedRoute><BadgesPage /></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><ContentLibraryPage /></ProtectedRoute>} />
      <Route path="/diet-recovery" element={<ProtectedRoute><DietRecoveryPage /></ProtectedRoute>} />
      <Route path="/diet" element={<DietRedirect />} />
      <Route path="/inbox" element={<ProtectedRoute><AdminInboxPage /></ProtectedRoute>} />
      <Route path="/group-chat" element={<ProtectedRoute><GroupChatPage /></ProtectedRoute>} />
      <Route path="/dm" element={<ProtectedRoute><DirectMessagesPage /></ProtectedRoute>} />
      <Route path="/dm/:otherUserId" element={<ProtectedRoute><DirectMessagesPage /></ProtectedRoute>} />
      <Route path="/bot" element={<ProtectedRoute><BotChatPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/relapse-recovery" element={<ProtectedRoute><RelapseRecoveryPage /></ProtectedRoute>} />
      <Route path="/women-only" element={<ProtectedRoute><WomenOnlyPage /></ProtectedRoute>} />
      <Route path="/men-only" element={<ProtectedRoute><MenOnlyPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/superadmin" element={<ProtectedRoute><SuperAdminPage /></ProtectedRoute>} />
      <Route path="/superadmin/arp-uploads" element={<ProtectedRoute><ARPUploadsPage /></ProtectedRoute>} />
      <Route path="/superadmin/family-course" element={<ProtectedRoute><SuperAdminFamilyCoursePage /></ProtectedRoute>} />
      <Route path="/family-course" element={<FamilyCoursePage />} />
      <Route path="/pleasure-or-problem" element={<IndividualCoursePage />} />
      <Route path="/pleasure-or-problem/reflect" element={<ReadinessCheckPage />} />
      {/* Ungated crisis-help route: the /crisis-resources path is kept
          alive as a redirect so any old link/bookmark lands on the single
          16-country /get-help directory (Sep 2026, Tudor Fix 2). */}
      <Route path="/crisis-resources" element={<Navigate to="/get-help" replace />} />
      {/* Public "Get help now" support-services page (Sep 2026 spec): no sign-in gate. */}
      <Route path="/get-help" element={<GetHelpPage />} />
      <Route path="/superadmin/checkin-report" element={<ProtectedRoute><SuperAdminCheckinReport /></ProtectedRoute>} />
      {/* Friendly aliases for print / socials: preserve query string for UTM tracking. */}
      <Route path="/plus" element={<Navigate to={`/pleasure-or-problem${window.location.search}`} replace />} />
      <Route path="/free-course" element={<Navigate to={`/pleasure-or-problem${window.location.search}`} replace />} />
      <Route path="/privacy-policy" element={<ProtectedRoute><PrivacyPolicyPage /></ProtectedRoute>} />
      <Route path="/terms-of-service" element={<ProtectedRoute><TermsOfServicePage /></ProtectedRoute>} />
      <Route path="/hipaa-privacy" element={<ProtectedRoute><HIPAAPrivacyPage /></ProtectedRoute>} />
      <Route path="/legal-review" element={<ProtectedRoute><HIPAAPrivacyPage /></ProtectedRoute>} />
      <Route path="/journey" element={<ProtectedRoute><JourneyPage /></ProtectedRoute>} />
      <Route path="/personal-history" element={<ProtectedRoute><PersonalHistoryPage /></ProtectedRoute>} />
      <Route path="/case-management" element={<ProtectedRoute><CaseManagementPage /></ProtectedRoute>} />
      <Route path="/admission-assessment" element={<ProtectedRoute><AdmissionAssessmentPage /></ProtectedRoute>} />
      <Route path="/drug-screen" element={<ProtectedRoute><DrugScreenPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/stickman-assessment" element={<ProtectedRoute><StickmanAssessmentPage /></ProtectedRoute>} />
      <Route path="/self-work/stickman" element={<ProtectedRoute><StickmanAssessmentPage /></ProtectedRoute>} />
      <Route path="/self-work/task/:taskId" element={<ProtectedRoute><SelfWorkTaskPage /></ProtectedRoute>} />
      <Route path="/self-work/:kind/:week" element={<ProtectedRoute><SelfWorkWorkbookPage /></ProtectedRoute>} />
      <Route path="/self-work" element={<ProtectedRoute><SelfWorkPage /></ProtectedRoute>} />
      <Route path="/introduction" element={<ProtectedRoute><IntroductionPage /></ProtectedRoute>} />
      <Route path="/references" element={<ProtectedRoute><ReferencesPage /></ProtectedRoute>} />
      <Route path="/readings/:readingId" element={<ProtectedRoute><WeeklyReadingPage /></ProtectedRoute>} />
      <Route path="/lessons" element={<ProtectedRoute><LessonsPage /></ProtectedRoute>} />
      <Route path="/lessons/:lessonId" element={<ProtectedRoute><LessonViewPage /></ProtectedRoute>} />
      <Route path="/games" element={<ProtectedRoute><GamesPage /></ProtectedRoute>} />
      {/* Legacy /games/wordscape, /games/color-sort, /games/quote-builder
          retired 9 Oct 2026.  Static games now live under /games/*.html. */}
      <Route path="/user-manual" element={<ProtectedRoute><UserManualPage /></ProtectedRoute>} />
      <Route path="/re-engagement" element={<ProtectedRoute><ReEngagementPage /></ProtectedRoute>} />
      <Route path="/relapse-message" element={<ProtectedRoute><RelapseMessagePage /></ProtectedRoute>} />
      <Route path="/relapse-prevention" element={<ProtectedRoute><RelapsePreventionPage /></ProtectedRoute>} />
      {/* UDS / Pathology module — Doctors tab removed Feb 2026.
          User-side /uds hidden until a real pathology partner is connected
          (ARP brief Feb 2026). */}
      <Route path="/superadmin/uds" element={<ProtectedRoute><SuperAdminUdsPage /></ProtectedRoute>} />
      <Route path="/pathology-upload/:token" element={<PathologyUploadPage />} />
      <Route path="/my-reports" element={<ProtectedRoute><MyReportsPage /></ProtectedRoute>} />
      <Route path="/superadmin/reports" element={<ProtectedRoute><SuperAdminReportsPage /></ProtectedRoute>} />
      <Route path="/" element={<LandingPage />} />
      </Routes>
      {/* Floating "Report Bug" button: only renders for authenticated users.
          BugReportButton itself checks auth context and returns null otherwise. */}
      <BugReportButton />
    </>
  );
}

export default AppRouter;